import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { subsectionNumber } from '../../utils/contentTree';

const collectExpandableNodes = (nodes = [], result = {}) => {
  nodes.forEach((node) => {
    if ((node.subsections || []).length > 0) {
      result[node.id] = true;
      collectExpandableNodes(node.subsections, result);
    }
  });
  return result;
};

const cleanNumberedTitle = (title = '') => {
  const value = String(title).trim();
  const cleaned = value.replace(/^\d+(?:\.\d+)*\.?\s*/, '').trim();
  return cleaned || value;
};

const RecursiveSidebarNodes = ({
  nodes = [],
  numberingPrefix,
  chapterIndex,
  sectionIndex,
  selectedItem,
  onSelect,
  depth = 3,
  selectedItemRef,
  _orderPath = [],
  _expandedNodes,
  _setExpandedNodes,
}) => {
  const [localExpandedNodes, setLocalExpandedNodes] = useState(() =>
    collectExpandableNodes(nodes)
  );
  const expandedNodes = _expandedNodes || localExpandedNodes;
  const setExpandedNodes = _setExpandedNodes || setLocalExpandedNodes;

  useEffect(() => {
    if (_expandedNodes) {
      return;
    }
    const expandable = collectExpandableNodes(nodes);
    setLocalExpandedNodes((current) => ({ ...expandable, ...current }));
  }, [nodes, _expandedNodes]);

  return nodes.map((node, index) => {
    const order = Number.isFinite(Number(node.order))
      ? Number(node.order)
      : index;
    const path = [..._orderPath, order];
    const number = subsectionNumber(numberingPrefix, path);
    const title = cleanNumberedTitle(node.title);
    const children = node.subsections || [];
    const hasChildren = children.length > 0;
    const isExpanded = hasChildren && expandedNodes[node.id] !== false;
    const isSelected =
      selectedItem?.type === 'subsection' &&
      (selectedItem?.subsectionId === node.id ||
        (!selectedItem?.subsectionId &&
          selectedItem?.chapterIndex === chapterIndex &&
          selectedItem?.sectionIndex === sectionIndex &&
          selectedItem?.subsectionIndex === path[0]));

    const toggle = () => {
      setExpandedNodes((current) => ({
        ...current,
        [node.id]: current[node.id] === false,
      }));
    };

    return (
      <div
        key={node.id ?? `${depth}-${path.join('-')}`}
        className="recursive-subsection-item"
        style={{ paddingLeft: `${Math.min(Math.max(depth - 3, 0) * 12, 48)}px` }}
      >
        <div
          className={`recursive-subsection-row ${
            isSelected ? 'selected' : ''
          }`}
        >
          {hasChildren ? (
            <button
              type="button"
              className="toggle-button"
              onClick={toggle}
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Réduire' : 'Développer'} ${number} ${title}`}
            >
              {isExpanded ? (
                <ChevronDown size={16} className="toggle-icon-expanded" />
              ) : (
                <ChevronRight size={16} className="toggle-icon-collapsed" />
              )}
            </button>
          ) : (
            <span className="recursive-subsection-toggle-spacer" aria-hidden="true" />
          )}

          <button
            type="button"
            ref={isSelected ? selectedItemRef : null}
            className="recursive-subsection-select"
            aria-current={isSelected ? 'location' : undefined}
            onClick={() =>
              onSelect({
                type: 'subsection',
                subsectionId: node.id,
                subsectionPath: path,
                subsectionIndex: path[0],
                chapterIndex,
                sectionIndex,
                data: node,
                numbering: number,
              })
            }
          >
            {number} {title}
          </button>
        </div>

        {isExpanded && (
          <RecursiveSidebarNodes
            nodes={children}
            numberingPrefix={numberingPrefix}
            chapterIndex={chapterIndex}
            sectionIndex={sectionIndex}
            selectedItem={selectedItem}
            onSelect={onSelect}
            depth={depth + 1}
            selectedItemRef={selectedItemRef}
            _orderPath={path}
            _expandedNodes={expandedNodes}
            _setExpandedNodes={setExpandedNodes}
          />
        )}
      </div>
    );
  });
};

export default RecursiveSidebarNodes;
